import React, { useState } from "react";
import styled from "styled-components";
import { useGetCustomFormByIdQuery } from "../../../api/formsApiSlice";
import { FormFieldControl } from "../../FormBuilder/FieldPreviews/FormFieldControl";
import { FieldDataTypeEnum, DEFAULT_BASIC_CONFIG } from "../../../types/types";
import type { FormPage, FormFieldTest } from "../../../types/types";
import type { FormRow as IFormRow } from "../../../types/types";
import { useTheme } from "../../../middleware/ThemeContext";
import StyledText from "../../StyledComponents/StyledText";
import { Theme } from "../../../utils/themes";

interface FormRenderComponentProps {
  formId: string | number;
  formName?: string;
}

const FormRenderComponent: React.FC<FormRenderComponentProps> = ({
  formId,
  formName,
}) => {
  const { theme } = useTheme();
  const [currentStep, setCurrentStep] = useState(0);

  // Convert formId to number if it's a string
  const numericFormId = typeof formId === 'string' ? parseInt(formId, 10) : formId;

  // // Fetch form data
  // const { data: formData, isLoading, error, isFetching } = useGetCustomFormByIdQuery(
  //   numericFormId,
  //   { skip: !numericFormId || isNaN(numericFormId as number), refetchOnMountOrArgChange: true }
  // );
  const shouldSkip =
    numericFormId == null || Number.isNaN(numericFormId);

  const { data: formData, isLoading, error, isFetching } =
    useGetCustomFormByIdQuery(numericFormId, {
      skip: shouldSkip,
      refetchOnMountOrArgChange: true,
    });

  // Handle different API response structures
  const form = formData?.data?.form || formData?.form || formData;
  const currentPage = form?.formPages?.[currentStep];

  if (isLoading || isFetching) {
    return (
      <LoadingContainer theme={theme}>
        <StyledText fontSize="14px" color="#666">
          Loading form...
        </StyledText>
      </LoadingContainer>
    );
  }

  if (error) {
    console.error('Form render error:', error);
    return (
      <ErrorContainer theme={theme}>
        <StyledText fontSize="14px" color="#d32f2f">
          Error loading form: {String(error)}
        </StyledText>
      </ErrorContainer>
    );
  }

  if (!form) {
    return (
      <EmptyContainer theme={theme}>
        <StyledText fontSize="14px" color="#666">
          Form not found. FormID: {numericFormId}
        </StyledText>
      </EmptyContainer>
    );
  }

  const renderField = (formField: FormFieldTest) => {
    const normalizeFormField = (ff: FormFieldTest): FormFieldTest | null => {
      if (!ff) return null;

      // If a real field exists and it's display-only, prefer config on the wrapper
      if (ff.field) {
        if (ff.field.dataType === FieldDataTypeEnum.DISPLAY_ONLY) {
          return {
            ...ff,
            field: {
              ...ff.field,
              config: ff?.config ?? ff.field?.config ?? DEFAULT_BASIC_CONFIG,
            },
          };
        }
        return ff;
      }

      // No field object but a component exists (heading/paragraph/divider) — synthesize a display-only field
      if (ff.component) {
        const compName = String(ff.component?.name || ff.componentName || "").toLowerCase();
        let fieldType: string | null = null;
        if (compName.includes("paragraph")) fieldType = "PARAGRAPH";
        else if (compName.includes("heading") || compName.includes("header")) fieldType = "HEADING";
        else if (compName.includes("divider")) fieldType = "DIVIDER";

        if (fieldType) {
          const syntheticField: any = {
            id: -(ff.id ?? 0),
            name: ff.component?.name || ff.componentName || "",
            dataType: FieldDataTypeEnum.DISPLAY_ONLY,
            fieldType,
            config: ff?.config ?? DEFAULT_BASIC_CONFIG,
          };
          return {
            ...ff,
            field: syntheticField,
          };
        }
      }

      return ff;
    };

    const normalized = normalizeFormField(formField);
    if (!normalized || (!normalized.field && !normalized.component)) return null;

    return (
      <FormFieldControl
        formField={normalized}
        value=""
        onChange={() => { }}
        forPreview
      />
    );
  };

  const renderMultipleFieldsRow = (row: IFormRow) => (
    <div key={row?.id} className="row px-2 component-row">
      <div className="col-12">
        <div className="component-container p-0 pb-1">
          <div className="component-fields-grid">
            {row?.formFields.map((formField, fieldIndex) => (
              <div key={`${row?.id}-${fieldIndex}`} className="component-field">
                {renderField(formField)}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderSingleOrSplitRow = (row: IFormRow) => {
    const leftField = row?.formFields.find((f) => f?.alignment === 1);
    const rightField = row?.formFields.find((f) => f?.alignment === 2);

    return (
      <FormRowContainer key={row.id}>
        <div className={row.layout === "SINGLE" ? "col-12" : "col-6"}>
          <RowFieldWrapper>
            {row.layout === "SINGLE"
              ? row.formFields[0] && renderField(row?.formFields[0])
              : leftField && renderField(leftField)}
          </RowFieldWrapper>
        </div>

        {row.layout === "MULTIPLE" && (
          <div className="col-6">
            {rightField && (
              <RowFieldWrapper>
                {renderField(rightField)}
              </RowFieldWrapper>
            )}
          </div>
        )}
      </FormRowContainer>
    );
  };

  const renderRow = (row: IFormRow) => {
    if (
      row?.layout === "MULTIPLE" &&
      row?.formFields.every((f) => f?.alignment === 0)
    ) {
      return renderMultipleFieldsRow(row);
    }
    return renderSingleOrSplitRow(row);
  };

  const sortedPages = [...(form?.formPages || [])].sort((a, b) => {
    const pageA = a.pageNumber ?? 0;
    const pageB = b.pageNumber ?? 0;
    return pageA - pageB;
  });

  const handleStepChange = (step: number) => setCurrentStep(step);

  const renderStepper = () => {
    if (sortedPages.length <= 1) return null;

    return (
      <TopStepperContainer theme={theme}>
        <StepperWrapper>
          {sortedPages?.map((page, index) => (
            <div
              className={index === sortedPages.length - 1 ? "" : "w-100"}
              key={index}
            >
              <StepperStep className="d-flex align-items-center">
                <div>
                  <StepCircle
                    active={index <= currentStep}
                    onClick={() => handleStepChange(index)}
                  >
                    {index + 1}
                  </StepCircle>
                </div>

                {index !== sortedPages.length - 1 && (
                  <Connector active={index < currentStep} />
                )}
              </StepperStep>
              <StepLabel
                theme={theme}
                active={index <= currentStep}
              >
                {page?.title}
              </StepLabel>
            </div>
          ))}
        </StepperWrapper>
      </TopStepperContainer>
    );
  };

  const renderEmptyState = () => (
    <EmptyPreviewState theme={theme}>
      <StyledText fontSize="16px" fontWeight={600} color="#666">
        This page is empty
      </StyledText>
      <StyledText fontSize="14px" color="#999">
        No fields have been added to this page
      </StyledText>
    </EmptyPreviewState>
  );

  return (
    <FormContainer theme={theme}>
      <FormHeader theme={theme} className="d-flex align-items-center justify-content-between px-3 py-3 border-bottom">
        <StyledText fontSize="16px" fontWeight={700} color={theme.CorporateTheme?.colors?.headerColor}>
          {formName || form?.title || "Form"}
        </StyledText>
      </FormHeader>

      {sortedPages.length > 1 && renderStepper()}

      <FormPreviewContainer theme={theme} className="px-3 py-3">
        {currentPage?.formRows && currentPage.formRows.length > 0
          ? currentPage.formRows.map(renderRow)
          : renderEmptyState()}
      </FormPreviewContainer>
    </FormContainer>
  );
};

// Styled Components
const FormContainer = styled.div<{ theme: Theme }>`
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
  overflow: hidden;
`;

const FormHeader = styled.div<{ theme: Theme }>`
  background: ${({ theme }) => theme.CorporateTheme.colors.background};
  border-bottom: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
`;

const TopStepperContainer = styled.div<{ theme: Theme }>`
  margin-bottom: 10px;
  border-radius: 8px;
  background: ${({ theme }) => theme.CreateFormTheme.colors.background};
`;

const StepperWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  max-width: 600px;
  margin: auto;
  overflow-x: auto;
  padding: 30px 0px 30px 30px;
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const StepperStep = styled.div`
  display: flex;
  align-items: center;
  min-width: 80px;
`;

const StepCircle = styled.div<{ active: boolean }>`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: ${({ active }) => (active ? "#002f6c" : "#c0c0c0")};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;
  cursor: pointer;
`;

const StepLabel = styled.div<{ active: boolean; theme: Theme }>`
  margin-top: 5px;
  font-size: 14px;
  font-weight: ${({ active }) => (active ? 700 : 500)};
  font-family: "Raleway", sans-serif;
  color: ${({ theme }) => theme.CorporateTheme.colors.textPrimary};
  white-space: nowrap;
`;

const Connector = styled.div<{ active: boolean }>`
  height: 4px;
  margin: 0 5px;
  background-color: ${({ active }) => (active ? "#002f6c" : "#c0c0c0")};
  width: 100%;
  border-radius: 2px;
`;

const FormPreviewContainer = styled.div<{ theme: Theme }>`
  min-height: 60vh;
  height: 60vh;
  max-height: 60vh;
  overflow-y: auto;
  background: ${({ theme }) => theme.CorporateTheme.colors.background};
  border-radius: 8px;
`;

const FormRowContainer = styled.div`
  display: flex;
`;

const RowFieldWrapper = styled.div`
  width: 100%;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  min-height: 48px;
  box-sizing: border-box;
  gap: 8px;

  & > * {
    width: 100%;
  }
`;

const LoadingContainer = styled.div<{ theme: Theme }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 300px;
  background: ${({ theme }) => theme.CorporateTheme.colors.background};
  border-radius: 8px;
`;

const ErrorContainer = styled(LoadingContainer)`
  height: 200px;
`;

const EmptyContainer = styled(LoadingContainer)`
  height: 200px;
`;

const EmptyPreviewState = styled.div<{ theme: Theme }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  text-align: center;
  background: ${({ theme }) => theme.CorporateTheme.colors.formBuilderNormalBG};
  border-radius: 8px;
  margin: 20px;
`;

export default FormRenderComponent;
